import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../reports.service';
import { InbuiltUtilityService } from '../inbuilt-utility.service';
import * as d3 from 'd3';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Colour palette applied in order to each legend series. Cycles if there are more than 6 series. */
const STACK_PALETTE: Array<{ backgroundColor: string; textColor: string; borderColor: string }> = [
  { backgroundColor: '#78C89F', textColor: '#fff' , borderColor: '#409769'},
  { backgroundColor: '#E85850', textColor: '#fff', borderColor: '#c7463f' },
  { backgroundColor: '#f8c78b', textColor: '#000', borderColor: '#b9833f' },
];

@Component({
  selector: 'app-inbuilt-stack-chart',
  templateUrl: './inbuilt-stack-chart.component.html',
  styleUrl: './inbuilt-stack-chart.component.css',
  standalone: false
})
export class InbuiltStackChartComponent implements OnInit, OnDestroy {
  @Input() config: any;
  @Input() elementData: any = null;
  @Output() headingResolved = new EventEmitter<string>();
  @ViewChild('stackChartContainer', { static: true }) private stackChartContainer!: ElementRef;
  @Input() Temp: boolean = false;
  chartData: any[] = [];
  legendItems: Array<{ key: string; backgroundColor: string; textColor: string }> = [];
  loading = false;

  // ── Tooltip state — driven by Angular template binding (avoids D3/SVG stacking issues) ──
  tooltipVisible = false;
  tooltipMonth    = '';
  tooltipItems: Array<{ key: string; value: string; isCurrent: boolean; color: string }> = [];

  private apiChartConfig: any = null;
  private columnInfo: any = null;
  private readonly destroy$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;
  private selectedFilters: any = {};
  private inputSearchFilter: string = '';
  private lastSelectedFiltersSignature = '';

  constructor(
    private reportsService: ReportsService,
    private inbuiltUtilityService: InbuiltUtilityService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.selectedFilters = this.config?.selectedFilters || {};
    this.lastSelectedFiltersSignature = JSON.stringify({ filters: this.selectedFilters, search: this.inputSearchFilter });
    this.subscribeToFilterEvents();
    this.callApi();
  }

  ngAfterViewInit(): void {
    const host = this.stackChartContainer?.nativeElement;
    if (!host || typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver(() => {
      if (this.chartData.length) this.buildAndRender();
    });
    this.resizeObserver.observe(host);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Filter subscription ──────────────────────────────────────────────────────────────────────

  private subscribeToFilterEvents(): void {
    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.shouldApplyFilterEvent(event)) return;
        const nextFilters = event?.selectedFilters || {};
        const nextInputSearchFilter = event?.inputSearchFilter || '';
        const nextSignature = JSON.stringify({ filters: nextFilters, search: nextInputSearchFilter });
        if (nextSignature === this.lastSelectedFiltersSignature) return;
        this.selectedFilters = nextFilters;
        this.inputSearchFilter = nextInputSearchFilter;
        this.lastSelectedFiltersSignature = nextSignature;
        this.callApi();
      });
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.config) return false;
    const eventReport = String(event?.reportNameFromQueryParams ?? event?.report ?? '');
    const eventPage   = String(event?.currentPage?.key ?? event?.page ?? '');
    const eventTab    = String(event?.currentTab?.key  ?? event?.tab  ?? '');
    const myReport    = String(this.config?.reportNameFromQueryParams ?? '');
    const myPage      = String(this.config?.currentPage?.key ?? '');
    const myTab       = String(this.config?.currentTab?.key  ?? '');
    if (eventReport && eventReport !== myReport) return false;
    if (eventPage   && eventPage   !== myPage)   return false;
    if (eventTab    && eventTab    !== myTab)     return false;
    return true;
  }

  // ─── API call ───────────────────────────────────────────────────────────────────────────────

  private callApi(): void {
    this.loading = true;
    let payload = {
      report:          this.config?.reportNameFromQueryParams,
      page:            this.config?.currentPage  ? this.config.currentPage.key  : '',
      tab:             this.config?.currentTab   ? this.config.currentTab.key   : '',
      elementName:     this.elementData?.name    || '',
      elementType:     this.elementData?.type    || 'stakeholder_chart',
      download:        false,
      selectedFilters: this.inbuiltUtilityService.getTablePayload(this.selectedFilters),
      inputSearchFilter: this.inputSearchFilter
    };
    if (this.Temp) {
      payload = this.elementData;
    }
    this.reportsService.getInbuildTableData(payload, this.Temp)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.loading = false; })
      )
      .subscribe({
        next: (res: any) => {
          this.apiChartConfig = res?.chartConfig ?? null;
          this.columnInfo     = res?.columnInfo  ?? null;
          this.chartData      = Array.isArray(res?.data) ? res.data : [];
          this.bindDisplayText(res?.dynamicHeaderTitle ?? null);
          // Defer rendering so Angular finishes the view update first.
          setTimeout(() => this.buildAndRender(), 0);
        },
        error: (err: any) => {
          console.error('Stack chart API error:', err);
          this.chartData  = [];
          this.legendItems = [];
          this.clearChart();
        }
      });
  }

  // ─── Chart config helpers ──────────────────────────────────────────────────────────────

  private normalizeHeaderKey(value: any): string {
    return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private resolveDynamicHeaderTitle(dynamicHeaderTitle: any): string {
    if (!dynamicHeaderTitle) {
      return '';
    }

    // API may return a plain string — use it directly
    if (typeof dynamicHeaderTitle === 'string') {
      return dynamicHeaderTitle.trim();
    }

    if (typeof dynamicHeaderTitle !== 'object' || Array.isArray(dynamicHeaderTitle)) {
      return '';
    }

    // Object form: match by element key (same as stat-card)
    const elementKey = String(this.elementData?.name ?? '').trim();
    const strippedKey = elementKey.startsWith('{') && elementKey.endsWith('}')
      ? elementKey.slice(1, -1).trim()
      : elementKey;

    const normalizedTarget = this.normalizeHeaderKey(strippedKey || elementKey);

    const matchedKey = Object.keys(dynamicHeaderTitle).find((key: string) =>
      this.normalizeHeaderKey(key) === normalizedTarget
    );

    if (matchedKey) {
      return String(dynamicHeaderTitle[matchedKey] ?? '').trim();
    }

    const firstResolvedValue = Object.values(dynamicHeaderTitle).find((value: any) =>
      typeof value === 'string' && String(value).trim() !== ''
    );

    return String(firstResolvedValue ?? '').trim();
  }

  bindDisplayText(dynamicHeaderTitle: any): void {
    const resolved = this.resolveDynamicHeaderTitle(dynamicHeaderTitle);
    if (!resolved) {
      return;
    }

    this.headingResolved.emit(resolved);
  }

  private get chartConfig(): any {
    return this.apiChartConfig ?? this.elementData?.chartConfig ?? {};
  }

  private get isCumulative(): boolean {
    const val = this.chartConfig?.isCumulative;
    if (typeof val === 'boolean') return val;
    return String(val ?? '').trim().toLowerCase() === 'true';
  }

  /**
   * Parses `chartConfig.legendData` which may be a JSON array, a Python-style
   * list string like "['Key A', 'Key B']", or already an Array.
   */
  private parseLegendData(): string[] {
    const raw = this.chartConfig?.legendData ?? '';
    if (Array.isArray(raw)) return raw.map((v: any) => String(v ?? '').trim()).filter(Boolean);
    const str = String(raw ?? '').trim();
    if (!str) return [];
    try {
      const parsed = JSON.parse(str.replace(/'/g, '"'));
      if (Array.isArray(parsed)) return parsed.map((v: any) => String(v ?? '').trim()).filter(Boolean);
    } catch { /* fall through */ }
    // Fallback: strip brackets, split by comma, remove surrounding quotes
    return str
      .replace(/^\[|\]$/g, '')
      .split(',')
      .map((v: string) => v.trim().replace(/^['"]+|['"]+$/g, ''))
      .filter(Boolean);
  }

  /** Case-insensitive search for the month field key in a sample row. */
  private resolveMonthKey(sample: any): string {
    const norm = (v: string) => v.toLowerCase().replace(/[\s_\-]+/g, '');
    return Object.keys(sample || {}).find((k: string) => norm(k) === 'month') ?? 'Month';
  }

  /** Returns 'SUM', 'COUNT', or 'UNKNOWN' by looking up the key in columnInfo. */
  private getMetricType(key: string): 'SUM' | 'COUNT' | 'UNKNOWN' {
    if (!this.columnInfo || typeof this.columnInfo !== 'object') return 'UNKNOWN';
    const norm = (v: string) => v.toLowerCase().replace(/[\s_\-]+/g, '');
    const matchedKey = Object.keys(this.columnInfo).find((k: string) => norm(k) === norm(key));
    if (!matchedKey) return 'UNKNOWN';
    const raw = this.columnInfo[matchedKey];
    // Support both plain string ("SUM") and object ({aggregate: "SUM"})
    const type = String(
      typeof raw === 'object' ? (raw?.aggregate ?? raw?.type ?? '') : raw ?? ''
    ).trim().toUpperCase();
    if (type === 'SUM') return 'SUM';
    if (type === 'COUNT') return 'COUNT';
    return 'UNKNOWN';
  }

  private formatValue(key: string, value: number): string {
    const type = this.getMetricType(key);
    return type === 'SUM' ? this.formatCompactCurrency(value) : this.formatCount(value);
  }

  private formatCompactCurrency(value: number): string {
    const abs  = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1_000_000_000) {
      const b = abs / 1_000_000_000;
      return `${sign}$${b % 1 === 0 ? b.toFixed(0) : b.toFixed(2)}B`;
    }
    if (abs >= 1_000_000) {
      const m = abs / 1_000_000;
      return `${sign}$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(2)}M`;
    }
    if (abs >= 1_000) {
      const k = abs / 1_000;
      return `${sign}$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(2)}K`;
    }
    return `${sign}$${abs % 1 === 0 ? abs.toFixed(0) : abs.toFixed(2)}`;
  }

  private formatCount(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
  }

  private toNumber(val: any): number {
    if (val === null || val === undefined || val === '') return 0;
    if (typeof val === 'number') return isFinite(val) ? val : 0;
    const n = parseFloat(String(val).replace(/[$,]/g, ''));
    return isFinite(n) ? n : 0;
  }

  // ─── Data preparation & render trigger ─────────────────────────────────────────────────

  private buildAndRender(): void {
    const rawSeriesKeys = this.parseLegendData();

    if (!this.chartData.length || !rawSeriesKeys.length) {
      this.legendItems = [];
      this.clearChart();
      return;
    }

    const monthKey = this.resolveMonthKey(this.chartData[0]);

    // Sort rows ascending by month number
    const sorted = [...this.chartData].sort(
      (a: any, b: any) => this.toNumber(a[monthKey]) - this.toNumber(b[monthKey])
    );

    // Build processed rows (apply cumulative accumulation if required)
    let processedRows: Array<{ monthLabel: string; [k: string]: any }>;
    let seriesKeys: string[];

    if (this.isCumulative) {
      // ── Step 1: accumulate values per key across months ─────────────────────────
      const acc: Record<string, number> = {};
      rawSeriesKeys.forEach((k: string) => (acc[k] = 0));
      processedRows = sorted.map((row: any) => {
        const monthNum = this.toNumber(row[monthKey]);
        const label = MONTH_LABELS[monthNum - 1] ?? String(monthNum);
        const result: any = { monthLabel: label };
        rawSeriesKeys.forEach((k: string) => {
          acc[k] += this.toNumber(row[k]);
          result[k] = acc[k];
        });
        return result;
      });

      // Preserve the original legendData order — no reordering.
      seriesKeys = rawSeriesKeys;
      console.log('[StackChart] isCumulative=true | processedRows:', processedRows);
    } else {
      processedRows = sorted.map((row: any) => {
        const monthNum = this.toNumber(row[monthKey]);
        const label = MONTH_LABELS[monthNum - 1] ?? String(monthNum);
        const result: any = { monthLabel: label };
        rawSeriesKeys.forEach((k: string) => { result[k] = this.toNumber(row[k]); });
        return result;
      });
      seriesKeys = rawSeriesKeys;
    }

    // Build legend binding using the final (possibly reordered) keys so the
    // legend colour matches the stacking order rendered in the chart.
    this.legendItems = seriesKeys.map((key: string, i: number) => ({
      key,
      ...STACK_PALETTE[i % STACK_PALETTE.length]
    }));

    this.renderChart(processedRows, seriesKeys);
  }

  // ─── D3 rendering ────────────────────────────────────────────────────────────────────────────

  private clearChart(): void {
    const host = this.stackChartContainer?.nativeElement;
    if (!host) return;
    d3.select(host).selectAll('*').remove();
    this.tooltipVisible = false;
  }

  private renderChart(
    rows: Array<{ monthLabel: string; [k: string]: any }>,
    seriesKeys: string[]
  ): void {
    const host = this.stackChartContainer?.nativeElement;
    if (!host) return;
    d3.select(host).selectAll('*').remove();

    const margin   = { top: 16, right: 16, bottom: 40, left: 8 };
    const hostW    = host.clientWidth  || 600;
    const hostH    = Math.max(240, host.clientHeight || 300);
    const innerW   = Math.max(100, hostW - margin.left - margin.right);
    const innerH   = Math.max(120, hostH - margin.top  - margin.bottom);

    d3.select(host).style('position', 'relative');

    // ── SVG — fills the chart canvas ─────────────────────────────────────────────────
    const svg = d3.select(host)
      .append('svg')
      .attr('width', hostW)
      .attr('height', hostH)
      .attr('viewBox', `0 0 ${hostW} ${hostH}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('display', 'block');

    const chart = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // ── Stacked data ─────────────────────────────────────────────────────────────────────
    const stackGen = d3.stack<any>()
      .keys(seriesKeys)
      .value((d: any, key: string) => this.toNumber(d[key]));
    const stacked = stackGen(rows);

    // Y max = top of tallest bar (last layer's highest point)
    const maxY = d3.max(stacked[stacked.length - 1], (d: any) => d[1]) ?? 0;

    // ── Scales ───────────────────────────────────────────────────────────────────────
    const xScale = d3.scaleBand()
      .domain(rows.map((r: any) => r.monthLabel))
      .range([0, innerW])
      .padding(0.12);

    const yScale = d3.scaleLinear()
      .domain([0, maxY > 0 ? maxY * 1.1 : 1])
      .nice()
      .range([innerH, 0]);

    // ── X axis (bottom) ──────────────────────────────────────────────────────────────
    chart.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(xScale).tickSizeOuter(0))
      .call((g: any) => g.select('.domain').attr('stroke', '#cbd5e1'))
      .call((g: any) => g.selectAll('.tick line').remove())
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', '#334155');

    // ── Stacked bar layers ────────────────────────────────────────────────────────────
    // Pre-compute adjusted pixel positions using a cursor that walks from the bottom of
    // the chart upward for each bar.  This guarantees:
    //   • Non-zero segments are at least MIN_SEG_PX pixels tall (always visible).
    //   • Segments never overlap each other (each one starts exactly where the
    //     previous one ended — no large segment can paint over a small one).
    const MIN_SEG_PX = 4;

    // monthAdjusted[monthIndex][layerIndex] = { y, height } in SVG pixels.
    // Two-pass algorithm:
    //   Pass 1 – compute natural pixel heights and collect "small" segments
    //            (value > 0 but sub-pixel height).
    //   Pass 2 – scale small-segment heights proportionally so that relative
    //            differences (e.g. $371M vs $32M) remain visible even though
    //            both are tiny compared to a $360B "Remaining" segment.
    //   Pass 3 – walk a cursor bottom→top to assign non-overlapping integer
    //            pixel positions (avoids sub-pixel rendering gaps).
    const monthAdjusted: Array<Array<{ y: number; height: number }>> = rows.map((row: any) => {
      const monthLabel = row.monthLabel;

      // Pass 1 – natural heights (-1 = datum absent, 0 = zero value, >0 = pixels)
      const datums = stacked.map((layer: any) =>
        layer.find((dd: any) => dd.data.monthLabel === monthLabel) ?? null
      );
      const naturalH: number[] = datums.map(datum => {
        if (!datum) return -1;
        const sv = datum[1] - datum[0];
        if (sv <= 0) return 0;
        return Math.max(0, yScale(datum[0]) - yScale(datum[1]));
      });

      // Pass 2 – proportional min-height for sub-pixel visible segments
      const actualH: number[] = naturalH.slice(); // copy; mutate only small segments
      const smallIdxs = naturalH.map((h, i) => (h > 0 && h < MIN_SEG_PX) ? i : -1).filter(i => i >= 0);
      if (smallIdxs.length > 0) {
        const vals   = smallIdxs.map(i => { const d = datums[i]; return d ? Math.max(0, d[1] - d[0]) : 0; });
        const total  = vals.reduce((s, v) => s + v, 0);
        const budget = smallIdxs.length * MIN_SEG_PX; // total px budget for all small segs
        smallIdxs.forEach((li, idx) => {
          const share = total > 0 ? vals[idx] / total : 1 / smallIdxs.length;
          actualH[li] = Math.max(2, Math.round(share * budget)); // min 2px per visible segment
        });
      }

      // Pass 3 – cursor walk (integers only → no sub-pixel gaps between adjacent rects)
      let cursorY = Math.round(innerH);
      return stacked.map((_layer: any, li: number) => {
        if (naturalH[li] <= 0) return { y: cursorY, height: 0 };
        const h    = Math.round(actualH[li]);
        const segY = cursorY - h;
        cursorY    = segY;
        return { y: segY, height: h };
      });
    });

    stacked.forEach((layer: any, layerIndex: number) => {
      const seriesKey = seriesKeys[layerIndex];
      const palette   = STACK_PALETTE[layerIndex % STACK_PALETTE.length];

      // Bars
      chart.append('g')
        .selectAll('rect')
        .data(layer)
        .enter()
        .append('rect')
        .attr('x',      (d: any) => xScale(d.data.monthLabel) ?? 0)
        .attr('y',      (_d: any, di: number) => monthAdjusted[di][layerIndex].y)
        .attr('height', (_d: any, di: number) => monthAdjusted[di][layerIndex].height)
        .attr('width',  xScale.bandwidth())
        .attr('fill',        palette.backgroundColor)
        .attr('stroke',       palette.borderColor)
        .attr('stroke-width', '1')
        // Hide zero-height segments completely — otherwise stroke renders as a visible line gap.
        .attr('display', (_d: any, di: number) => monthAdjusted[di][layerIndex].height === 0 ? 'none' : null)
        // D3 v5: handler signature is (datum, index) — NOT (event, datum) like v6/v7.
        // Arrow function keeps 'this' bound to the component class.
        .on('mousemove', (d: any) => {
          console.log('[StackChart] mousemove fired | month:', d.data['monthLabel'], '| row data:', d.data);
          this.tooltipMonth = String(d.data['monthLabel'] ?? '');
          this.tooltipItems = seriesKeys.map((k: string, i: number) => ({
            key:       k,
            value:     this.formatValue(k, this.toNumber(d.data[k])),
            isCurrent: k === seriesKey,
            color:     STACK_PALETTE[i % STACK_PALETTE.length].backgroundColor
          }));
          this.tooltipVisible = true;
          this.cdr.detectChanges();
        })
        .on('mouseleave', () => {
          this.tooltipVisible = false;
          this.cdr.detectChanges();
        });

      // Value labels (only rendered when the segment is tall enough to fit text)
      chart.append('g')
        .selectAll('text')
        .data(layer)
        .enter()
        .append('text')
        .attr('x', (d: any) => (xScale(d.data.monthLabel) ?? 0) + xScale.bandwidth() / 2)
        .attr('y', (_d: any, di: number) => {
          const pos = monthAdjusted[di][layerIndex];
          return pos.y + pos.height / 2 + 4;
        })
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('fill', palette.textColor)
        .attr('pointer-events', 'none')
        .text((d: any, di: number) => {
          const pos = monthAdjusted[di][layerIndex];
          if (pos.height < 14) return '';
          return this.formatValue(seriesKey, d[1] - d[0]);
        });
    });
  }
}


